import { useState, useEffect, useMemo } from 'react'
import type { Account, Category } from '@zeni/shared'
import { useLanguage } from '../contexts/LanguageContext'
import { categoriesApi } from '../lib/api'
import {
  FORMAT_LABEL_KEYS,
  FORMAT_NOTE_KEYS,
  formatsForAccount,
  importRowsInChunks,
  parseInstitutionCsvFile,
  type InstitutionFormat,
} from '../lib/csv-import'
import { btnSecondary, modalTitle } from '../lib/ui'

interface CsvImportModalProps {
  open: boolean
  /** 取り込み先の口座。カードから開くので取り込み先は選ばせない */
  account: Account | null
  token: string
  onClose: () => void
  /** インポート成功後。残高が変わるので呼び出し側で口座を再取得する */
  onImported: () => void
}

// 口座カードから開くCSVインポート。取り込み先はその口座に固定し、
// 口座の金融機関に対応するフォーマットだけを候補に出す。
export function CsvImportModal({ open, account, token, onClose, onImported }: CsvImportModalProps) {
  const { t } = useLanguage()
  const [categories, setCategories] = useState<Category[]>([])
  const [format, setFormat] = useState<InstitutionFormat | ''>('')
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const formats = useMemo(() => (account ? formatsForAccount(account) : []), [account])

  useEffect(() => {
    if (!open) return
    setFormat(formats.length === 1 ? formats[0] : '')
    setResult(null)
    setError(null)
  }, [open, formats])

  useEffect(() => {
    if (!open || !token) return
    void categoriesApi.list(token).then((res) => setCategories(res.data)).catch(() => undefined)
  }, [open, token])

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0 || !account || !format) return
    setImporting(true)
    setResult(null)
    setError(null)
    try {
      let imported = 0
      let skipped = 0
      for (const file of files) {
        const rows = await parseInstitutionCsvFile(file, format, account.id, categories)
        if (rows.length === 0) {
          throw new Error(t('csv_import_file_no_rows').replace('{name}', file.name))
        }
        const res = await importRowsInChunks(token, rows)
        imported += res.imported
        skipped += res.skipped
      }
      setResult(
        t('transactions_csv_imported_result')
          .replace('{count}', String(imported))
          .replace('{skipped}', String(skipped)),
      )
      onImported()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setImporting(false)
      e.target.value = ''
    }
  }

  if (!open || !account) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 dark:bg-gray-900">
        <h2 className={modalTitle}>
          {t('accounts_csv_import_title').replace('{name}', account.name)}
        </h2>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
            {t('csv_import_format_label')}
          </label>
          {formats.length > 1 ? (
            <select
              value={format}
              onChange={(e) => { setFormat(e.target.value as InstitutionFormat); setResult(null); setError(null) }}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">{t('accounts_csv_import_format_placeholder')}</option>
              {formats.map((f) => (
                <option key={f} value={f}>{t(FORMAT_LABEL_KEYS[f])}</option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {formats.length === 1 ? t(FORMAT_LABEL_KEYS[formats[0]]) : t('accounts_csv_import_no_format')}
            </p>
          )}
          {format && (
            <p className="text-xs text-gray-400 mt-2 dark:text-gray-500">{t(FORMAT_NOTE_KEYS[format])}</p>
          )}
        </div>

        {result && <p className="mt-4 text-sm text-green-600 dark:text-green-400">{result}</p>}
        {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className={btnSecondary}
          >
            {t('common_cancel')}
          </button>
          <label
            className={`px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg cursor-pointer hover:bg-indigo-700 transition-colors ${
              importing || !format ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            {importing ? t('csv_importing') : t('csv_import_btn')}
            <input
              type="file"
              accept=".csv"
              multiple
              className="hidden"
              onChange={(e) => { void handleImport(e) }}
            />
          </label>
        </div>
      </div>
    </div>
  )
}
